/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import { isEmpty } from 'lodash';

import type { Theme } from '@coursera/cds-core';

import type { Asset, EntWebsitePartner, Maybe } from 'bundles/ent-website/__generated__/baseContentfulTypes';
import Image from 'bundles/ent-website/contentful/core/Image';

export type PortraitType = Maybe<{ __typename?: 'Asset' | undefined } & Pick<Asset, 'url'>> | undefined;

export type Organization =
  | Maybe<
      {
        __typename?: 'EntWebsitePartner' | undefined;
      } & Pick<EntWebsitePartner, 'slug' | 'description' | 'name'> & {
          logo?:
            | Maybe<{
                __typename?: 'Asset' | undefined;
              }>
            | undefined;
        }
    >
  | undefined;

type PortraitProps = {
  portrait: PortraitType;
  organization?: Organization;
};

function Portrait({ portrait, organization, ...otherProps }: PortraitProps) {
  const styles: Record<string, Interpolation<Theme>> = {
    portraitContainer: () => css`
      position: relative;
      padding: var(--cds-spacing-150);
      margin: auto;
    `,
    portrait: () => css`
      position: absolute;
      top: var(--cds-spacing-150);
      left: var(--cds-spacing-150);
      right: var(--cds-spacing-150);
      bottom: var(--cds-spacing-150);
      border-radius: 50%;
      overflow: hidden;
    `,
    portraitImage: css`
      position: absolute;
      display: inline-block;
      top: 0;
      left: 0;
      min-height: 100%;
      min-width: 100%;
      width: 100%;
      object-fit: cover;
    `,
    portraitBorder: () => css`
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 2px solid var(--cds-color-grey-50);
      border-radius: 50%;
    `,
    organizationWrapper: css`
      display: block;
      position: absolute;
      bottom: 0;
      right: -12px;
      width: 48px;
      height: 48px;
    `,
    organization: () => css`
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 1px solid var(--cds-color-grey-50);
      border-radius: 50%;
      overflow: hidden;
    `,
    organizationImage: css`
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      height: 100%;
      max-width: 100%;
      overflow: hidden;
      background: var(--cds-color-white-0);
      z-index: 1;
      object-fit: cover;
    `,
  };

  // Appeasing TS with additional check
  if (isEmpty(portrait) || !portrait) {
    return null;
  }

  return (
    <div css={styles.portraitContainer} {...otherProps}>
      <div css={styles.portraitBorder} />
      <div css={styles.portrait}>
        <Image content={portrait} css={styles.portraitImage} width="auto" height="auto" />
      </div>

      {organization?.logo && (
        <div css={styles.organizationWrapper}>
          <div css={styles.organization}>
            <Image content={organization.logo} css={styles.organizationImage} />
          </div>
        </div>
      )}
    </div>
  );
}

export default Portrait;
